import { BrowserModule } from "@angular/platform-browser";
import { APP_INITIALIZER, InjectionToken, NgModule } from "@angular/core";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HttpClientModule } from '@angular/common/http';
import { RouterModule } from "@angular/router";
import { NgxPaginationModule } from "ngx-pagination";
import { ToastrModule } from "ngx-toastr";
import { AppComponent } from "./app.component";
import { NavMenuComponent } from "./nav-menu/nav-menu.component";
import { HomeComponent } from "./home/home.component";
import { HeaderComponent } from "./nav-menu/header.component";
import { WantedCreditsComponent } from "./wanted-credits/wanted-credits.component";
import { CreditOffersComponent } from "./credit-offers/credit-offers.component";
import { SearchComponent } from "./wanted-credits/search.component";
import { SpinnerWheelComponent } from "./common/spinner-wheel/spinner-wheel.component";
import { AddressComponent } from "./common/components/address/address.component";
import { ContactDetailsComponent } from "./common/components/contact-details/contact-details.component";
import { InterceptorsModule } from "./core/interceptors/interceptors.module";
import { AlertModelComponent } from "./common/components/alert-model/alert-model.component";
import { ConfirmComponent } from "./common/components/confirm/confirm.component";
import { AccordionCollapsbleIconsDirective } from "./common/directives/accordion-collapsble-icons.directive";
import { AccordionDefaultOpenIconsDirective } from "./common/directives/accordion-default-open-icons.directive";
import { ExportFileComponent } from "./common/components/export-file/export-file.component";
import { PrivacyComponent } from "./privacy/privacy.component";
import { MsalGuard, MsalRedirectComponent } from "@azure/msal-angular";
import {ShapeFileModalComponent} from "./common/components/shape-file-modal/shape-file-modal.component"

import { getMsalProviders } from "./auth.providers";
import { AppConfigService } from "./services/app.config.service";
import {  UserAccountStatusComponent } from "./account-status/user-account-status.component";
import { userIsActive } from "./guards/user-status.guard";
import { ROUTES } from '../app/common/constants';
import { AccountProfileComponent } from "./account-profile/account-profile/account-profile.component";

const AUTH_CONFIG_URL_TOKEN = new InjectionToken<string>("AUTH_CONFIG_URL");
import { TooltipReaderComponent } from './common/components/tooltip-reader/tooltip-reader.component'
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { OffsetsPaymentComponent } from "./offsets-payment/offsets-payment.component";
import { CreditPruchaseComponent } from "./credit-pruchase/credit-pruchase.component";
import { ConfigService } from './core/services/config.service';
import { QuotesComponent } from './quotes/quotes.component';
import { PurchasePortalComponent } from './purchase-portal/purchase-portal.component';
import { RfqComponent } from './quotes/rfq/rfq.component';
import { RtmpComponent } from './quotes/rtmp/rtmp.component';
import { PaymentComponent } from './quotes/rtmp/payment.component';
import { ConfirmationComponent } from './quotes/rfq/confirmation.component';
import {RtmpConfirmationComponent} from './common/components/confirm/rtmp-confirmation.component'
import { QuoteComponent } from './quotes/rfq/quote.component';
import { AgGridModule } from 'ag-grid-angular-legacy';
import { CreditComponent } from './common/components/credit/credit.component';
import { MailingAddressComponent } from './common/components/mailing-address/mailing-address.component';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { AgGridCheckboxComponent } from './common/components/ag-grid-checkbox/ag-grid-checkbox.component';
import { CustomButtonCellrenderComponent } from './quotes/custom-button-cellrender/custom-button-cellrender.component';
import { UserAccessListComponent } from './common/components/user-access/user-access-list.component';
import { QuoteCreditPaymentGridsComponent } from './common/components/quote-credit-payment-grids/quote-credit-payment-grids.component';
import { TooltipDisplayComponent } from './common/components/tooltip-display/tooltip-display.component';
import { AgGridHeaderTooltipComponent } from './common/components/ag-grid-header-tooltip/ag-grid-header-tooltip.component';

const initializerFactory: (
  appConfigService: AppConfigService,
  configUrl: string
) => () => Promise<boolean> = (appConfigService, configUrl) =>
  () => appConfigService.initialize(configUrl);

@NgModule({
  declarations: [
    AppComponent,
    NavMenuComponent,
    HomeComponent,
    HeaderComponent,
    WantedCreditsComponent,
    CreditOffersComponent,
    SearchComponent,
    SpinnerWheelComponent,
    AddressComponent,
    ContactDetailsComponent,
    AlertModelComponent,
    ConfirmComponent,
    AccordionCollapsbleIconsDirective,
    AccordionDefaultOpenIconsDirective,
    ExportFileComponent,
    PrivacyComponent,
    UserAccountStatusComponent,
    AccountProfileComponent,
    TooltipReaderComponent,
    QuotesComponent,
    PurchasePortalComponent,
    RfqComponent,
    RtmpComponent,
    PaymentComponent,
    ConfirmationComponent,
    QuoteComponent,
    CreditComponent,
    MailingAddressComponent,
    AgGridCheckboxComponent,
    CustomButtonCellrenderComponent,
    UserAccessListComponent,
    ShapeFileModalComponent,
    QuoteCreditPaymentGridsComponent,
    TooltipDisplayComponent,
    AgGridHeaderTooltipComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: "ng-cli-universal" }),
    AgGridModule.withComponents([AgGridCheckboxComponent, AgGridHeaderTooltipComponent]),
    AutocompleteLibModule,
    HttpClientModule,
    NgbModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    InterceptorsModule.forRoot(),
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: "toast-top-right",
      preventDuplicates: true,
    }),
    RouterModule.forRoot([
      {
        path: ROUTES.home,
        component: HomeComponent,
      },
      {
        path: ROUTES.wantedCredits,
        component: WantedCreditsComponent,
        pathMatch: "full",
      },
      {
        path: ROUTES.creditOffer,
        component: CreditOffersComponent,
        pathMatch: "full",
        canActivate: [MsalGuard, userIsActive],
      },
      {
        path: ROUTES.confirm,
        component: ConfirmComponent,
        pathMatch: "full",
      },
      { 
        path: ROUTES.authenticationRedirect, 
        component: MsalRedirectComponent },
      {
        path: ROUTES.confirmCreditOffer,
        component: ConfirmComponent,
        pathMatch: "full",
      },
      {
        path: ROUTES.userAccountStatus,
        component: UserAccountStatusComponent,
      },
      {
        path: ROUTES.userAccountProfile,
        component: AccountProfileComponent,
        canActivate: [MsalGuard, userIsActive],
      },
      {
        path: ROUTES.creditPruchase,
        component: CreditPruchaseComponent,
        canActivate: [MsalGuard, userIsActive],
      },
      {
        path: ROUTES.offsetsPayment,
        component: OffsetsPaymentComponent,
        canActivate: [MsalGuard, userIsActive],
      },
      { path: 'credit-confirm/:offerNumber', component: ConfirmComponent, pathMatch: 'full' },
      { path: 'quotes', component: QuotesComponent },
      { path: 'quotes/rfq/confirmation', component: ConfirmationComponent },
      { path: 'quotes/rfq/:id', component: RfqComponent },
      { path: 'quotes/rfq', component: RfqComponent },
      { path: 'rtmp/confirmation', component: RtmpConfirmationComponent},
      { path: 'quotes/rtmp/:id', component: RtmpComponent, canActivate: [MsalGuard], }
    ]),
  ],
  exports: [
    AlertModelComponent,
    AccordionCollapsbleIconsDirective,
    AccordionDefaultOpenIconsDirective,
    ShapeFileModalComponent
  ],
  providers: [
    ...getMsalProviders(),
    { provide: AUTH_CONFIG_URL_TOKEN, useValue: "/api/app/configuration" },
    {
      provide: APP_INITIALIZER,
      useFactory: initializerFactory,
      deps: [AppConfigService, AUTH_CONFIG_URL_TOKEN],
      multi: true,
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}

